import './PlayActionProgress.css';

const labels = {
  route: 'Planning your route', habitat: 'Assessing the surroundings', catalog: 'Preparing local encounter tables',
  encounters: 'Checking the journey', reactions: 'Resolving events', observations: 'Resolving examinations',
  response: 'Resolving responses', harvest: 'Resolving gathering', condition: 'Checking stored materials',
  resolving: 'Resolving your action', recognition: 'Reviewing your search results',
};

// Presentation only. Ordinary actions and combat animate locally; only the
// action transport supplies progress after the server accepts a long action.
export default function PlayActionProgress({ active, completed = false, progress, keepVisible = false, compact = false }) {
  const className = `play-action-progress${compact ? ' play-action-progress-compact' : ''}`;
  if (!active) return completed ? <div className={className} aria-busy="false">
    <div className="play-action-progress-label" role="status" aria-live="polite"><strong>Turn Complete</strong></div>
  </div> : null;
  const activeGroups = progress?.groups?.filter(group => group.completed + group.failed < group.total);
  if (activeGroups && !activeGroups.length && !progress.label && !keepVisible) return null;
  if (activeGroups && (activeGroups.length || !keepVisible)) return <div className={className} aria-busy="true">
    {progress.label && <div className="play-action-progress-label" role="status">{progress.label}</div>}
    {activeGroups.map(group => {
      const finished = group.completed + group.failed, work = group.work;
      const measured = work?.total > 0 && Number.isFinite(work.completed);
      const percent = measured ? Math.min(100, Math.max(0, Math.floor(work.completed / work.total * 100)))
        : group.total ? Math.round(finished / group.total * 100) : 0;
      return <div key={group.label} className="play-action-progress-group">
        <div className="play-action-progress-label" role="status" aria-live="polite">
          <strong>{group.label}</strong><span>{measured
            ? `${Math.floor(work.completed)} of ${Math.ceil(work.total)} ${work.unit}${Number.isFinite(work.rounds) ? ` · ${work.rounds} rounds processed` : ''}`
            : `${group.completed} of ${group.total} completed${group.failed ? ` · ${group.failed} failed` : ''}`}</span>
        </div>
        <div className={`play-action-progress-track${!measured && finished < group.total ? ' indeterminate' : ''}`} role="progressbar"
          aria-label={`${group.label} progress`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={percent}>
          <div style={measured || finished === group.total ? { width: `${percent}%` } : undefined} />
        </div>
      </div>;
    })}
  </div>;
  const counted = progress?.total > 0 && progress?.completed != null;
  const percent = counted ? Math.round(progress.completed / progress.total * 100) : null;
  // Play3 keeps its request open across chained tasks and persistence/review
  // boundaries. Finished groups do not mean that the request has completed.
  const label = activeGroups ? progress.label || 'Processing...'
    : progress ? labels[progress.stage] || 'Resolving your action' : 'Processing...';
  return <div className={className} aria-busy="true">
    <div className="play-action-progress-label" role="status" aria-live="polite">
      <strong>{label}</strong>
      {counted && <span>{progress.completed} of {progress.total} {({ catalog: 'assessment batches', reactions: 'event assessments', harvest: 'gathering attempts', condition: 'material batches' })[progress.stage] || 'travel checks'} processed</span>}
    </div>
    <div className={`play-action-progress-track${counted ? '' : ' indeterminate'}`} role="progressbar"
      aria-label="Action processing progress" aria-valuemin={0} aria-valuemax={100}
      {...(counted ? { 'aria-valuenow': percent } : {})}>
      <div style={counted ? { width: `${percent}%` } : undefined} />
    </div>
    {progress?.connectionIssue && <div className="play-action-progress-detail" role="status">Reconnecting to action progress...</div>}
  </div>;
}
